<script lang="ts">
	//Hooks
	import { onMount, getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { parseSchedules } from 'src/helpers/actions/schedules/core/schedule_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { doesSupportLocalActions } from 'src/helpers/actions/local/supported/local_action_supported_helpers';
	import { doesSupportVirtualLocalActions } from 'src/helpers/actions/local/core/local_action_virtual_helpers';
	import { filterScheduleMethodByChannel } from 'src/helpers/actions/schedules/supported/schedule_supported_helpers';
	import {
		getPeripheralDeviceStatus,
		isPeripheralDevice,
	} from 'src/helpers/addons/addon_device_helpers';
	//Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Components
	import SchedulesListV2 from './SchedulesListV2.svelte';
	import CreateSchedulesG2 from './CreateSchedulesG2.svelte';
	import NoPeripheralsAlert from 'src/components/ThePill/NoPeripheralsAlert.svelte';
	//Design system components
	import { Button, Alert, Loader, Toggle } from '@Shelly/shelly-design-system';
	//Helpers
	import { isPill, isProCover } from 'src/helpers/interface_helpers';
	import { getAddonStatus } from 'src/helpers/addons/addon_helpers';
	import { getScheduleList } from 'src/helpers/get_schedules_gen_2';
	//Types
	import type {
		SchedulesG2Request,
		ScheduleParams,
		ScheduleRequestData,
	} from 'src/types/requests/schedules/schedules_g2';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Peripheral } from 'src/types/Peripherals';

	export let device: Device,
		switchPeripheral: Peripheral = null,
		isOffline = false;

	const deviceId = device.id;
	const deviceChannels =
		device.mode == 'roller' && !isProCover(device.type) ? 1 : device.channels_count;
	const doesSupportSchedules =
		doesSupportLocalActions(device) || doesSupportVirtualLocalActions(device);

	const getSchedules = async () => {
		waitForResponse = true;
		scheduleListError = '';
		schedules = [];
		unsupported = [];
		allChannelsSchedules = [];
		allChannelsUnsupported = [];
		const res: SchedulesG2Request = await getScheduleList(device);

		if (res?.isok) {
			const jobs = res?.data?.jobs || res?.jobs || [];
			Object.values(jobs).forEach((schedule) => {
				const params = parseSchedules(schedule),
					deviceChannel = switchPeripheral
						? switchPeripheral?.sensor?.extid
						: device.category == 'emeter'
							? 0
							: device.channel;
				if (params && params.calls.find((i) => i?.method)) {
					if (
						params.calls.find((job) =>
							filterScheduleMethodByChannel(device, job, switchPeripheral, deviceChannel),
						)
					) {
						schedules.push(params);
					}
					if (device.channel < 100) {
						allChannelsSchedules.push(params);
					}
				} else {
					if (
						params.calls.find((job) =>
							filterScheduleMethodByChannel(device, job, switchPeripheral, deviceChannel),
						)
					) {
						unsupported.push(schedule);
					}
					if (device.channel < 100) {
						allChannelsUnsupported.push(schedule);
					}
				}
			});
		} else {
			scheduleListError = $format('alert_danger_sched_list_error', {
				default: "Could not load the schedules' list.",
			});
		}
		waitForResponse = false;
	};

	const createSchedule = (data: ScheduleParams = null) => {
		if (data == null) {
			const schedule: ScheduleRequestData = {
				enable: true,
				timespec: '0 0 0 * * *',
				calls: [],
			};
			data = parseSchedules(schedule);
		}
		selectedSchedule = data;
		showWizard = true;
	};

	const deleteAction = async (data: ScheduleParams) => {
		const res: SettingRequest = await gen2Request(device, 'schedule.delete', {
			id: data.id,
		});
		if (res.isok) {
			schedules = schedules.filter((i) => i.id != data.id);
			allChannelsSchedules = allChannelsSchedules.filter((i) => i.id != data.id);
			unsupported = unsupported.filter((i) => i.id != data.id);
			allChannelsUnsupported = unsupported.filter((i) => i.id != data.id);
		}
		onSettingsResponse(res, device, {
			success: $format('action_deleted_scc_toast', {
				default: 'Action was deleted.',
			}),
			danger: $format('action_delete_failed_toast', {
				default: 'Failed to delete action!',
			}),
		});
	};

	const toggleAction = async (data: ScheduleParams) => {
		const res: SettingRequest = await gen2Request(device, 'schedule.update', {
			id: data.id,
			enable: data.enable,
		});
		onSettingsResponse(res, device, {
			success: data.enable
				? $format('schedule_enabled_scc_toast', {
						default: 'Schedule enabled.',
					})
				: $format('schedule_disabled_scc_toast', {
						default: 'Schedule disabled.',
					}),
		});
	};

	let schedules: ScheduleParams[] = [],
		allChannelsSchedules: ScheduleParams[] = [],
		unsupported: ScheduleRequestData[] = [],
		allChannelsUnsupported: ScheduleRequestData[] = [],
		showWizard = false,
		selectedSchedule: ScheduleParams = null,
		scheduleListError = '',
		missingSwitchPeripheral = false,
		waitForResponse = false,
		showSchedulesForAllChannels = false;

	onMount(() => {
		if (isPeripheralDevice(device.type) && device.category == 'relay') {
			switchPeripheral = getPeripheralDeviceStatus(device)?.[0];
		} else if (isPill(device.type)) {
			switchPeripheral = (getAddonStatus(device) || []).find((i) => i?.peripheralType == 'switch');
			missingSwitchPeripheral = switchPeripheral == null;
		}
		if (doesSupportSchedules && !isOffline) getSchedules();
	});

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if (
			$showNewLayout &&
			doesSupportSchedules &&
			!waitForResponse &&
			!scheduleListError &&
			!missingSwitchPeripheral
		) {
			stickyBottomAction.set({
				onClick: () => createSchedule(),
				loading: false,
				disabled: false,
				label: $format('button_add_schedule', { default: 'Add schedule' }),
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if waitForResponse}
	<div class="d-flex justify-content-center">
		<Loader />
	</div>
{:else if scheduleListError}
	<Alert type="danger" heading={scheduleListError} class="mb-s" />
	<Button
		size="m"
		class="width-full mr-s text-capitalized"
		responsiveSize={{
			sm: 'm',
		}}
		variant="primary"
		on:click={getSchedules}
	>
		{$format('button_rescan', { default: 'Rescan' })}
	</Button>
{:else if missingSwitchPeripheral}
	<NoPeripheralsAlert {device} />
{:else}
	{#if allChannelsSchedules.length && allChannelsSchedules.length != schedules.length}
		<Toggle
			class="mb-s ml-s"
			size="m"
			checked={showSchedulesForAllChannels}
			on:click={() => (showSchedulesForAllChannels = !showSchedulesForAllChannels)}
			id="toggle-5bckber"
			>{$format('schedule_show_all_device_channels', {
				default: 'Show schedules for all device channels',
			})}</Toggle
		>
	{/if}
	{#if doesSupportSchedules}
		{#if !showSchedulesForAllChannels && !schedules.length && !unsupported.length}
			<Alert
				heading={$format('alert_heading_no_schedules')}
				message={$format('alert_message_first_add_schedule', {
					default: 'If you want to use this feature, you first need to add a schedule.',
				})}
				isHeadingBold
			/>
		{:else}
			<SchedulesListV2
				list={showSchedulesForAllChannels ? allChannelsSchedules : schedules}
				unsupported={showSchedulesForAllChannels ? allChannelsUnsupported : unsupported}
				{deviceId}
				{deviceChannels}
				on:edit={(e) => createSchedule(e.detail)}
				on:delete={(e) => deleteAction(e.detail)}
				on:toggle={(e) => {
					toggleAction(e.detail);
				}}
			/>
		{/if}
		{#if !$showNewLayout}
			<div class="pt-s">
				<Button size="m" width="full" on:click={() => createSchedule()}>
					{$format('button_add_schedule', {
						default: 'Add schedule',
					})}
				</Button>
			</div>
		{/if}
	{:else}
		<Alert
			class="mb-s"
			heading={$format('alert_message_can_be_scheduled', {
				default: 'There are no actions that can be scheduled.',
			})}
			isHeadingBold
		/>
	{/if}
{/if}
{#if showWizard}
	<CreateSchedulesG2
		{device}
		{switchPeripheral}
		schedule={selectedSchedule}
		bind:showWizard
		on:save={getSchedules}
	/>
{/if}
